import { JSONParser, TokenType } from '@streamparser/json';
import { MAX_PROJECT_MANIFEST_BYTES } from './CurrentProjectFormat';

const HEADER = 16;
const CHUNK = 64 * 1024;

function length(header: Uint8Array): number {
  if (header.length !== HEADER) throw Error('Truncated project header');
  const view = new DataView(header.buffer, header.byteOffset, header.byteLength);
  const size = view.getUint32(8, true);
  if (view.getUint32(12, true) || !size || size > MAX_PROJECT_MANIFEST_BYTES)
    throw Error('Invalid project manifest size');
  return size;
}

/** History needs full identity checks for valid large manifests. Stream only the
 * declared metadata, never the runtime blocks or a hydrated project graph. */
async function* manifest(file: Blob, signal?: AbortSignal) {
  signal?.throwIfAborted();
  const header = new Uint8Array(await file.slice(0, HEADER).arrayBuffer());
  signal?.throwIfAborted();
  const magic = new TextDecoder().decode(header.subarray(0, 8));
  if (/^DGTLCMP[2-6]$/.test(magic)) {
    const size = length(header);
    if (file.size < HEADER + size) throw Error('Truncated project manifest');
    yield { version: Number(magic.at(-1)) };
    for (let offset = HEADER; offset < HEADER + size; offset += CHUNK) {
      signal?.throwIfAborted();
      yield new Uint8Array(
        await file.slice(offset, Math.min(offset + CHUNK, HEADER + size)).arrayBuffer(),
      );
    }
    return;
  }
  if (!/^DGTLZLB[56]$/.test(magic) || header.length !== HEADER)
    throw Error('Unknown project format');
  const outer = new DataView(header.buffer);
  if (outer.getUint32(12, true)) throw Error('Invalid compressed project header');
  const expanded = outer.getUint32(8, true);
  const stream = new DecompressionStream('deflate');
  const reader = stream.readable.getReader();
  const writer = stream.writable.getWriter();
  let stopped = false;
  const stop = () => {
    stopped = true;
    return Promise.allSettled([reader.cancel(), writer.abort()]);
  };
  const abort = () => {
    void stop();
  };
  signal?.addEventListener('abort', abort, { once: true });
  const pump = (async () => {
    // Allow deflate framing overhead, but never scan an unbounded compressed source.
    const budget = MAX_PROJECT_MANIFEST_BYTES + 1024 * 1024;
    for (let offset = HEADER; offset < file.size && !stopped; offset += 4096) {
      signal?.throwIfAborted();
      if (offset - HEADER >= budget) throw Error('Compressed metadata budget');
      const bytes = await file
        .slice(offset, Math.min(offset + 4096, HEADER + budget))
        .arrayBuffer();
      if (!stopped) await writer.write(new Uint8Array(bytes));
    }
    if (!stopped) await writer.close();
  })().catch(async () => {
    await reader.cancel().catch(() => {});
  });
  const inner = new Uint8Array(HEADER);
  let read = 0;
  let remaining: number | undefined;
  try {
    while (true) {
      signal?.throwIfAborted();
      const { done, value } = await reader.read();
      signal?.throwIfAborted();
      if (done) throw Error('Truncated compressed metadata');
      let offset = 0;
      if (read < HEADER) {
        offset = Math.min(HEADER - read, value.length);
        inner.set(value.subarray(0, offset), read);
        read += offset;
        if (read < HEADER) continue;
        if (
          new TextDecoder().decode(inner.subarray(0, 8)) !== magic.replace('ZLB', 'CMP')
        )
          throw Error('Unexpected compressed project version');
        remaining = length(inner);
        if (HEADER + remaining > expanded) throw Error('Invalid expansion size');
        yield { version: Number(magic.at(-1)) };
      }
      const count = Math.min(value.length - offset, remaining!);
      if (count) yield value.subarray(offset, offset + count);
      remaining! -= count;
      if (!remaining) return;
    }
  } finally {
    await stop();
    await pump;
    signal?.removeEventListener('abort', abort);
    reader.releaseLock();
    writer.releaseLock();
  }
}

/** A display-only identity check after a successful open. Keep the separate
 * attempt/privacy chooser hint's smaller budget and fail-open policy unchanged. */
export async function matchesOpenedProjectIdentity(
  raw: unknown,
  expected: string,
  signal?: AbortSignal,
): Promise<boolean> {
  try {
    if (!(raw instanceof Blob) || !expected) return false;
    const parser = new JSONParser({ keepStack: false });
    const keys = new WeakMap<object, Set<string | number>>();
    let depth = 0,
      version: unknown,
      containerVersion = 0,
      main: unknown;
    let root = false,
      project = false,
      chips = false,
      matches = 0,
      unsafe = false;
    parser.onToken = ({ token }) => {
      if (token === TokenType.LEFT_BRACE || token === TokenType.LEFT_BRACKET) {
        if (++depth > 128) throw Error('Manifest nesting limit');
      } else if (token === TokenType.RIGHT_BRACE || token === TokenType.RIGHT_BRACKET)
        --depth;
    };
    parser.onValue = ({ value, key, parent, stack }) => {
      if (parent && !Array.isArray(parent)) {
        const seen = keys.get(parent) ?? new Set<string | number>();
        if (seen.has(key!)) throw Error('Ambiguous project metadata');
        seen.add(key!);
        keys.set(parent, seen);
      }
      const path = [...stack.map((entry) => entry.key), key].filter(
        (entry) => entry !== undefined,
      );
      if (!path.length)
        root = value !== null && typeof value === 'object' && !Array.isArray(value);
      if (path.length === 1 && key === 'version') version = value;
      if (path.length === 1 && key === 'project')
        project = value !== null && typeof value === 'object' && !Array.isArray(value);
      if (path.length === 2 && path[0] === 'project' && key === 'mainChipId')
        main = value;
      if (path.length === 2 && path[0] === 'project' && key === 'chips')
        chips = Array.isArray(value);
      if (
        path.length === 4 &&
        path[0] === 'project' &&
        path[1] === 'chips' &&
        typeof path[2] === 'number' &&
        key === 'id' &&
        value === expected
      )
        matches++;
      if (
        path[0] === 'metadataScalars' &&
        typeof value === 'string' &&
        value.includes('mainChipId')
      )
        unsafe = true;
      // This parser retains emitting ancestors even with keepStack:false.
      // Release every inspected child explicitly; array keys are logical
      // counters, so clear the completed members rather than delete by index.
      if (parent) {
        if (Array.isArray(parent)) parent.length = 0;
        else delete parent[key!];
      }
    };
    let yielded = performance.now();
    for await (const part of manifest(raw, signal)) {
      signal?.throwIfAborted();
      if (part instanceof Uint8Array) parser.write(part);
      else containerVersion = part.version;
      if (performance.now() - yielded > 8) {
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
        yielded = performance.now();
      }
    }
    signal?.throwIfAborted();
    if (!parser.isEnded) parser.end();
    return (
      root &&
      project &&
      chips &&
      version === containerVersion &&
      main === expected &&
      matches === 1 &&
      !unsafe
    );
  } catch {
    signal?.throwIfAborted();
    return false;
  }
}
